import React, { useState, useContext, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    IconButton,
    Button,
    Stack,
    Divider,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Paper,
    CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import WorkIcon from '@mui/icons-material/Work';
import { MapPin, Calendar } from 'lucide-react';
import { AuthContext } from '../context/AuthContext';
import { ErrorContext } from '../context/ErrorContext';
import { createAxiosInstance } from '../api/axios';
import { hasCompletedPersonalInfo, getIncompletePersonalFields } from '../utils/profileValidation';

const JobDrawer = ({ open, onClose, jobId, isLoading, onJobLoaded, appliedJobIds = new Set() }) => {
    const [job, setJob] = useState(null);
    const [showProfileModal, setShowProfileModal] = useState(false);
    const [incompleteFields, setIncompleteFields] = useState([]);
    const [copySuccess, setCopySuccess] = useState(false);
    const [checkingProfile, setCheckingProfile] = useState(false);
    const { user } = useContext(AuthContext);
    const { setError } = useContext(ErrorContext);
    const navigate = useNavigate();
    // const theme = useTheme();

    const authAxios = useMemo(() => {
        return createAxiosInstance(null);
    }, []);

    const fetchJob = async () => {
        if (!jobId) return;
        
        try {
            const response = await authAxios.get(`/job/${jobId}/`);
            setJob(response.data);
            if (onJobLoaded) {
                onJobLoaded();
            }
        } catch (error) {
            if (onJobLoaded) {
                onJobLoaded();
            }
            if (error.response && error.response.status === 403) {
                setError("This job posting may require login to view, or it may not be publicly available");
            } else if (error.response && error.response.status === 404) {
                setError("Job posting not found");
            } else {
                setError("Failed to fetch job details");
            }
        }
    };

    const handleApply = async () => {
        if (!user) {
            // Redirect to auth page with the apply URL as redirect parameter
            const redirectUrl = encodeURIComponent(`/opportunities/${jobId}/apply`);
            navigate(`/auth/sign-in?redirect=${redirectUrl}`);
            return;
        }

        // Check if already applied
        if (isAlreadyApplied) {
            return; // Don't allow navigation if already applied
        }

        // Fetch latest user data before checking profile completion
        setCheckingProfile(true);
        try {
            const response = await authAxios.get('/self/');
            const latestUserData = response.data;

            // Check if profile is complete using latest data
            if (!hasCompletedPersonalInfo(latestUserData)) {
                const missingFields = getIncompletePersonalFields(latestUserData);
                setIncompleteFields(missingFields);
                setShowProfileModal(true);
                return;
            }

            window.open(`/opportunities/${jobId}/apply`, '_blank');
        } catch (error) {
            console.error('Failed to fetch latest user data:', error);
            // Fallback to cached user data if API fails
            if (!hasCompletedPersonalInfo(user)) {
                const missingFields = getIncompletePersonalFields(user);
                setIncompleteFields(missingFields);
                setShowProfileModal(true);
                return;
            }
            window.open(`/opportunities/${jobId}/apply`, '_blank');
        } finally {
            setCheckingProfile(false);
        }
    };

    const handleCopyLink = () => {
        const jobUrl = `${window.location.origin}/opportunities?opp_id=${jobId}`;
        navigator.clipboard.writeText(jobUrl).then(() => {
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 2000);
        });
    };

    const getLocationTypeDisplay = (locationType) => {
        const types = {
            'ONSITE': 'On-site',
            'HYBRID': 'Hybrid',
            'REMOTE': 'Remote'
        };
        return types[locationType] || locationType;
    };

    const getPaymentFrequencyDisplay = (frequency) => {
        const frequencies = {
            'YEARLY': '/ Year',
            'MONTHLY': '/ Month',
            'WEEKLY': '/ Week',
            'DAILY': '/ Day',
            'HOURLY': '/ Hour'
        };
        return frequencies[frequency] || '';
    };

    useEffect(() => {
        if (open && jobId) {
            // Reset job state when jobId changes to show loading immediately
            setJob(null);
            fetchJob();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, jobId]);

    const isAlreadyApplied = jobId && appliedJobIds.has(jobId);
    console.log("List of applied jobs IDs:", appliedJobIds);
    console.log("Is already applied:", isAlreadyApplied);

    if (!open) return null;

    if (isLoading || !job) {
        return (
            <Paper sx={{
                width: { xs: '100%', md: '50%' },
                height: '100vh',
                position: 'fixed',
                right: 0,
                top: 0,
                overflow: 'auto',
                zIndex: 1300,
                backgroundColor: '#f9fafc',
                boxShadow: 'none',
                borderRadius: 0,
                display: 'flex',
                flexDirection: 'column',
            }}>
                {/* Close Button */}
                <Box sx={{
                    position: 'absolute',
                    top: 12,
                    right: 12,
                    zIndex: 1,
                }}>
                    <IconButton onClick={onClose} size="small" sx={{ color: '#0f172a' }}>
                        <CloseIcon />
                    </IconButton>
                </Box>
                
                {/* Loading Content */}
                <Box sx={{ 
                    flex: 1, 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    flexDirection: 'column',
                    gap: 2,
                    pt: 7,
                }}>
                    <CircularProgress size={48} sx={{ color: 'primary.main' }} />
                    <Typography variant="body1" sx={{ color: '#000' }}>
                        Loading job details...
                    </Typography>
                </Box>
            </Paper>
        );
    }

    const Section = ({ title, children }) => (
        <Box sx={{ mb: 4 }}>
            <Typography
                variant="h6"
                sx={{
                    fontSize: '20px',
                    fontWeight: '600',
                    color: '#000',
                    marginBottom: '10px',
                }}
            >
                {title}
            </Typography>
            {children}
        </Box>
    );

    return (
        <Paper sx={{
            width: { xs: '100%', md: '50%' },
            height: '100vh',
            position: 'fixed',
            right: 0,
            top: 0,
            overflow: 'auto',
            zIndex: 1300,
            backgroundColor: '#f9fafc',
            boxShadow: 'none',
            borderRadius: 0,
            display: 'flex',
            flexDirection: 'column',
            // Styles for HTML content
            '& ul, & ol': {
                paddingLeft: '20px',
                margin: '8px 0',
            },
            '& ul': {
                listStyleType: 'disc',
                listStylePosition: 'outside',
            },
            '& ol': {
                listStyleType: 'decimal',
                listStylePosition: 'outside',
            },
            '& li': {
                marginBottom: '4px',
                lineHeight: 1.6,
                display: 'list-item',
            },
            '& p': {
                margin: '8px 0',
                lineHeight: 1.6,
            },
            '& strong': {
                fontWeight: 600,
            },
            '& em': {
                fontStyle: 'italic',
            },
            '& u': {
                textDecoration: 'underline',
            },
            '& h1, & h2, & h3, & h4, & h5, & h6': {
                margin: '16px 0 8px 0',
                fontWeight: 600,
                color: '#000',
            }
        }}>
            {/* Close Button */}
            <Box sx={{
                position: 'absolute',
                top: 12,
                right: 12,
                zIndex: 1,
            }}>
                <IconButton onClick={onClose} size="small" sx={{ color: '#0f172a' }}>
                    <CloseIcon />
                </IconButton>
            </Box>

            {/* Content */}
            <Box sx={{ 
                flex: 1, 
                overflow: 'auto',
                p: { xs: 3, md: 4 },
                pb: 10, // Extra padding for sticky button
                pt: 7, // Reduced from 6
            }}>
                {/* Job Title and Salary Row */}
                <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'flex-start',
                    mb: 3,
                    gap: 2,
                }}>
                    <Typography
                        variant="h5"
                        sx={{
                            fontSize: '24px',
                            fontWeight: '600',
                            color: '#000',
                            maxWidth: '50%',
                            wordWrap: 'break-word',
                        }}
                    >
                        {job.title}
                    </Typography>
                    {job.salary && (
                        <Typography
                            variant="h5"
                            sx={{
                                fontSize: '24px',
                                fontWeight: '600',
                                color: '#15803d',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {job.salary}
                            {job.payment_frequency && (
                                <Box component="span" sx={{ ml: 0.5 }}>
                                    {getPaymentFrequencyDisplay(job.payment_frequency)}
                                </Box>
                            )}
                        </Typography>
                    )}
                </Box>

                {/* Job Info */}
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={3}
                    sx={{ mb: 3 }}
                >
                    {/* Location Type and Location */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {job.job_location_type && (
                            <>
                                <Typography variant="body2" sx={{ color: '#000', fontWeight: 600 }}>
                                    {getLocationTypeDisplay(job.job_location_type)}
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#64748b' }}>
                                    •
                                </Typography>
                            </>
                        )}
                        <MapPin size={16} color="#000" />
                        <Typography variant="body2" sx={{ color: '#000', fontWeight: 500 }}>
                            {job.location}
                        </Typography>
                    </Box>

                    {/* Application Deadline */}
                    {job.application_deadline && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Calendar size={16} color="#000" />
                            <Typography variant="body2" sx={{ color: '#000', fontWeight: 500 }}>
                                Deadline: {new Date(job.application_deadline).toLocaleDateString()}
                            </Typography>
                        </Box>
                    )}
                </Stack>

                {/* Copy Link Section */}
                <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 2, 
                    p: 2, 
                    backgroundColor: '#f1f5f9',
                    borderRadius: 2,
                    border: '1px solid #e2e8f0',
                    mb: 4,
                }}>
                    <Typography 
                        variant="body1" 
                        sx={{ 
                            flex: 1, 
                            color: '#475569',
                            fontSize: '15px',
                            fontWeight: 500,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {`${window.location.origin}/opportunities?opp_id=${jobId}`}
                    </Typography>
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={<ContentCopyIcon />}
                        onClick={handleCopyLink}
                        sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            whiteSpace: 'nowrap',
                            minWidth: 'auto',
                        }}
                    >
                        {copySuccess ? 'Copied!' : 'Copy'}
                    </Button>
                </Box>

                <Divider sx={{ my: 3 }} />

                {/* Job Details Sections */}
                <Stack>
                    {job.about_company && job.about_company.trim() && (
                        <Section title="About Company">
                            <Typography 
                                variant="body1" 
                                sx={{ whiteSpace: 'pre-line', color: '#000' }}
                                dangerouslySetInnerHTML={{ __html: job.about_company }}
                            />
                        </Section>
                    )}

                    {job.description && job.description.trim() && (
                        <Section title="Job Description">
                            <Typography 
                                variant="body1" 
                                sx={{ whiteSpace: 'pre-line', color: '#000' }}
                                dangerouslySetInnerHTML={{ __html: job.description }}
                            />
                        </Section>
                    )}

                    {job.responsibilities && job.responsibilities.trim() && (
                        <Section title="Key Responsibilities">
                            <Typography 
                                variant="body1" 
                                sx={{ whiteSpace: 'pre-line', color: '#000' }}
                                dangerouslySetInnerHTML={{ __html: job.responsibilities }}
                            />
                        </Section>
                    )}

                    {job.qualifications && job.qualifications.trim() && (
                        <Section title="Required Qualifications">
                            <Typography 
                                variant="body1" 
                                sx={{ whiteSpace: 'pre-line', color: '#000' }}
                                dangerouslySetInnerHTML={{ __html: job.qualifications }}
                            />
                        </Section>
                    )}

                    {job.additional_qualifications && job.additional_qualifications.trim() && (
                        <Section title="Additional Qualifications">
                            <Typography 
                                variant="body1" 
                                sx={{ whiteSpace: 'pre-line', color: '#000' }}
                                dangerouslySetInnerHTML={{ __html: job.additional_qualifications }}
                            />
                        </Section>
                    )}

                    {job.terms_and_conditions && job.terms_and_conditions.trim() && (
                        <Section title="Terms and Conditions">
                            <Typography 
                                variant="body1" 
                                sx={{ whiteSpace: 'pre-line', color: '#000' }}
                                dangerouslySetInnerHTML={{ __html: job.terms_and_conditions }}
                            />
                        </Section>
                    )}

                    {job.benefits && job.benefits.trim() && (
                        <Section title="Additional Benefits">
                            <Typography 
                                variant="body1" 
                                sx={{ whiteSpace: 'pre-line', color: '#000' }}
                                dangerouslySetInnerHTML={{ __html: job.benefits }}
                            />
                        </Section>
                    )}
                </Stack>
            </Box>

            {/* Sticky Apply Button */}
            {job.is_active !== false && (
                <Box sx={{
                    position: 'sticky',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    p: 3,
                    backgroundColor: 'white',
                    borderTop: '1px solid #e0e0e0',
                    boxShadow: '0 -2px 8px rgba(0, 0, 0, 0.1)',
                }}>
                    {!user ? (
                        // Show login button for non-logged-in users
                        <Button
                            variant="contained"
                            fullWidth
                            size="large"
                            startIcon={<WorkIcon />}
                            onClick={() => {
                                const redirectUrl = encodeURIComponent(`/opportunities/${jobId}/apply`);
                                navigate(`/auth/sign-in?redirect=${redirectUrl}`);
                            }}
                            sx={{
                                fontWeight: 600,
                                py: 1.5,
                                borderRadius: 2,
                                textTransform: 'none',
                            }}
                        >
                            Log In to Start Application
                        </Button>
                    ) : (
                        // Show single button for logged-in users
                        <Button
                            variant={isAlreadyApplied ? "outlined" : "contained"}
                            fullWidth
                            size="large"
                            startIcon={checkingProfile ? <CircularProgress size={20} color="inherit" /> : <WorkIcon />}
                            onClick={handleApply}
                            disabled={isAlreadyApplied || checkingProfile}
                            sx={{
                                fontWeight: 600,
                                py: 1.5,
                                borderRadius: 2,
                                textTransform: 'none',
                                ...(isAlreadyApplied && {
                                    backgroundColor: '#f5f5f5',
                                    color: '#9e9e9e',
                                    borderColor: '#e0e0e0',
                                    '&:hover': {
                                        backgroundColor: '#f5f5f5',
                                        borderColor: '#e0e0e0',
                                    },
                                    '&.Mui-disabled': {
                                        backgroundColor: '#f5f5f5',
                                        color: '#9e9e9e',
                                        borderColor: '#e0e0e0',
                                    }
                                })
                            }}
                        >
                            {checkingProfile ? 'Checking...' : (isAlreadyApplied ? 'Already Applied' : 'Start Application')}
                        </Button>
                    )}
                </Box>
            )}
            {/* Profile Completion Modal */}
            <ProfileCompletionModal 
                open={showProfileModal} 
                onClose={() => setShowProfileModal(false)}
                redirectUrl={`/opportunities/${jobId}/apply`}
                incompleteFields={incompleteFields}
            />
        </Paper>
    );
};

// Profile Completion Modal Component
const ProfileCompletionModal = ({ open, onClose, redirectUrl = null, incompleteFields = [] }) => (
    <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
    >
        <DialogTitle sx={{ textAlign: 'center', fontWeight: 600 }}>
            Complete Your Profile
        </DialogTitle>
        <DialogContent>
            <Typography variant="body1" sx={{ textAlign: 'center', mb: 2 }}>
                To apply for jobs, you need to complete the following profile information:
            </Typography>
            {incompleteFields.length > 0 && (
                <Box sx={{ 
                    bgcolor: '#fef2f2', 
                    border: '1px solid #fecaca', 
                    borderRadius: 2, 
                    p: 2, 
                    mt: 2 
                }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#dc2626', mb: 1 }}>
                        Missing Required Fields:
                    </Typography>
                    <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                        {incompleteFields.map((item, index) => (
                            <Typography 
                                component="li" 
                                key={index} 
                                variant="body2" 
                                sx={{ color: '#7f1d1d', mb: 0.5 }}
                            >
                                {item.label}
                            </Typography>
                        ))}
                    </Box>
                </Box>
            )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', gap: 2, pb: 3 }}>
            <Button
                onClick={onClose}
                variant="outlined"
                sx={{ minWidth: 100 }}
            >
                Close
            </Button>
            <Button
                onClick={() => {
                    onClose();
                    // If redirectUrl is provided, append it as a query parameter to manage-self
                    // const targetUrl = redirectUrl 
                    //     ? `/manage-self?redirect=${encodeURIComponent(redirectUrl)}`
                    //     : '/manage-self';
                    const targetUrl = redirectUrl 
                        ? `/profile?redirect=${encodeURIComponent(redirectUrl)}`
                        : '/profile';
                    window.location.href = targetUrl;
                }}
                variant="contained"
                sx={{ minWidth: 100 }}
            >
                Complete Profile
            </Button>
        </DialogActions>
    </Dialog>
);

export { ProfileCompletionModal };
export default JobDrawer;